import { RegistryWorkspace, RegistryTableSurface } from '@workspace/silk-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CommerceCatalogWorkspace,
  DescriptionText,
  HorizontalStack,
  RegistryStatus,
  VerticalStack,
  DataTable,
  DataTableToolbar,
  DataTableFacetedFilter,
  Checkbox,
  toast,
  type ColumnDef,
  type RowSelectionState,
  type SortingState,
} from '@workspace/silk-ui';
import { useScope } from '@workspace/scope-context';
import {
  Layers,
  Pencil,
  Plus,
  Store,
  Tag,
} from 'lucide-react';
import { useNavigation } from '@/context/NavigationContext';
import {
  listAccessPointLayers,
  createMarket,
  createMarketGroup,
  createServiceabilityZone,
  getCommerceErrorMessage,
  getCommerceErrorStatus,
  listMarkets,
  listMarketGroups,
  listServiceabilityZones,
  type AccessPointLayer,
  type Market as CommerceMarket,
  type MarketGroup,
  type ServiceabilityZone,
  updateMarket,
  updateMarketGroup,
  updateServiceabilityZone,
} from '@/lib/commerce';
import { listMapSources, type MapSource } from '@/lib/mapping-api';
import { defineCommerceRegistryColumns } from '@/lib/commerceRegistryColumns';
import {
  findSalesMapVectorLayer,
  pickBestSalesMapLayerSource,
} from '@/lib/salesMapSourcePackage';
import {
  lastPageIndexForTotal,
  toLimitOffset,
  type RoutePaginationState,
} from '@/lib/routePagination';
import {
  buildCommerceSectionRoute,
  buildCommerceShellViewModel,
  CommerceBreadcrumbHeader,
  CommerceRegistryUtilityBand,
} from '@/views/commerce/catalogHierarchyShared';
import {
  buildMarketSelectorOptions,
  buildMarketGroupSelectorOptions,
  buildServiceabilityZoneSelectorOptions,
} from '@/views/commerce/selectorOptions';
import {
  MarketsWizardEditor,
  type WorkspaceEditorKind,
  type ZoneFormState,
  type MarketFormState,
  type GroupFormState,
} from './MarketsWizardEditor';
import {
  ROUTE_PRICING_ACCESS_POINT_PURPOSES,
  type ZoneInfrastructureBinding,
} from './zoneInfrastructure';
import {
  hydrateRoutePricingBuildTypeRates,
  parseRoutePricingBuildTypeRates,
} from '@/features/commerce-admin/routePricingRateController';

// ─── Form helpers ─────────────────────────────────────────────────────────────

function formatJSONObject(value: Record<string, unknown> | undefined): string {
  return JSON.stringify(value ?? {}, null, 2);
}

function parseJSONObject(value: string, label: string): Record<string, unknown> {
  const trimmed = value.trim();
  if (!trimmed) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    throw new Error(`${label} must be valid JSON.`);
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error(`${label} must be a JSON object.`);
  }
  return parsed as Record<string, unknown>;
}

function withoutLegacyPricebookMetadata(metadata: Record<string, unknown> | undefined): Record<string, unknown> {
  const { signup_pricebook_id: _signupPricebookId, pricebook_id: _pricebookId, ...rest } = metadata || {};
  return rest;
}

function toDatetimeLocal(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function toISOString(value: string, label: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) throw new Error(`${label} is invalid.`);
  return date.toISOString();
}

function createEmptyZoneForm(): ZoneFormState {
  return {
    zoneKey: '',
    name: '',
    zoneKind: 'serviceability_zone',
    sourceSystem: '',
    sourceRef: '',
    buildTypeRates: [],
    metadataJSON: formatJSONObject({}),
  };
}

function createEmptyMarketForm(): MarketFormState {
  return {
    zoneId: '', marketKey: '', name: '', status: 'inactive', installMethod: '',
    canSignUp: false, canScheduleInstall: false, estimatedReadyAt: '',
    customerMessage: '', marketGroupId: '', metadataJSON: formatJSONObject({}),
  };
}

function createEmptyGroupForm(): GroupFormState {
  return { marketGroupKey: '', name: '', marketIds: [], marketLabels: [], metadataJSON: formatJSONObject({}) };
}

function buildZoneForm(item: ServiceabilityZone): ZoneFormState {
  return {
    zoneKey: item.zone_key || '', name: item.name || '',
    zoneKind: item.zone_kind || 'serviceability_zone',
    sourceSystem: item.source_system || '', sourceRef: item.source_ref || '',
    buildTypeRates: hydrateRoutePricingBuildTypeRates(item.build_type_rates),
    metadataJSON: formatJSONObject(item.metadata),
  };
}

function buildMarketForm(item: CommerceMarket): MarketFormState {
  return {
    zoneId: item.zone_id || '', marketKey: item.market_key || '', name: item.name || '',
    status: item.status || 'inactive', installMethod: item.install_method || '',
    canSignUp: Boolean(item.can_sign_up), canScheduleInstall: Boolean(item.can_schedule_install),
    estimatedReadyAt: toDatetimeLocal(item.estimated_ready_at),
    customerMessage: item.customer_message || '', marketGroupId: item.market_group_id || '',
    metadataJSON: formatJSONObject(withoutLegacyPricebookMetadata(item.metadata)),
  };
}

function buildGroupForm(item: MarketGroup): GroupFormState {
  return {
    marketGroupKey: item.market_group_key || '', name: item.name || '',
    marketIds: item.market_ids || [], marketLabels: item.market_labels || [],
    metadataJSON: formatJSONObject(withoutLegacyPricebookMetadata(item.metadata)),
  };
}

function buildZonePayload(form: ZoneFormState): Record<string, unknown> {
  const parsedRates = parseRoutePricingBuildTypeRates(form.buildTypeRates, { label: 'Zone pricing override' });
  if (parsedRates.validationIssues[0]) throw new Error(parsedRates.validationIssues[0].message);
  const payload: Record<string, unknown> = {
    zone_key: form.zoneKey.trim(),
    name: form.name.trim(),
    zone_kind: form.zoneKind.trim() || 'serviceability_zone',
    metadata: parseJSONObject(form.metadataJSON, 'Zone metadata'),
    build_type_rates: parsedRates.rates,
  };
  if (form.sourceSystem.trim()) payload.source_system = form.sourceSystem.trim();
  if (form.sourceRef.trim()) payload.source_ref = form.sourceRef.trim();
  return payload;
}

function buildMarketPayload(form: MarketFormState): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    zone_id: form.zoneId.trim(), market_key: form.marketKey.trim(), name: form.name.trim(),
    status: form.status, can_sign_up: form.canSignUp, can_schedule_install: form.canScheduleInstall,
    market_group_id: form.marketGroupId,
    metadata: withoutLegacyPricebookMetadata(parseJSONObject(form.metadataJSON, 'Market metadata')),
  };
  if (form.installMethod.trim()) payload.install_method = form.installMethod.trim();
  if (form.customerMessage.trim()) payload.customer_message = form.customerMessage.trim();
  const era = toISOString(form.estimatedReadyAt, 'Estimated ready time');
  if (era) payload.estimated_ready_at = era;
  return payload;
}

function buildGroupPayload(form: GroupFormState): Record<string, unknown> {
  return {
    market_group_key: form.marketGroupKey.trim(), name: form.name.trim(),
    market_ids: form.marketIds,
    metadata: withoutLegacyPricebookMetadata(parseJSONObject(form.metadataJSON, 'Market group metadata')),
  };
}

function formatRelativeDate(value?: string | null): string {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
}

function hasZoneBindings(zone: ServiceabilityZone): boolean {
  const m = zone.metadata || {};
  return ['polygon', 'polygons', 'geometry', 'bounds', 'circle', 'google_place_ids', 'place_ids', 'location_codes'].some(k => m[k] !== undefined);
}

function compareInfrastructureLayers(left: AccessPointLayer, right: AccessPointLayer): number {
  const leftPurposeRank = ROUTE_PRICING_ACCESS_POINT_PURPOSES.indexOf(
    left.purpose as (typeof ROUTE_PRICING_ACCESS_POINT_PURPOSES)[number],
  );
  const rightPurposeRank = ROUTE_PRICING_ACCESS_POINT_PURPOSES.indexOf(
    right.purpose as (typeof ROUTE_PRICING_ACCESS_POINT_PURPOSES)[number],
  );
  const normalizedLeftRank = leftPurposeRank === -1 ? Number.MAX_SAFE_INTEGER : leftPurposeRank;
  const normalizedRightRank = rightPurposeRank === -1 ? Number.MAX_SAFE_INTEGER : rightPurposeRank;
  if (normalizedLeftRank !== normalizedRightRank) {
    return normalizedLeftRank - normalizedRightRank;
  }
  return (
    left.name.localeCompare(right.name) ||
    left.layer_key.localeCompare(right.layer_key) ||
    left.layer_id.localeCompare(right.layer_id)
  );
}

function buildZoneInfrastructureBindings(
  layers: AccessPointLayer[],
  mapSources: MapSource[],
): ZoneInfrastructureBinding[] {
  const canonicalFiberSource = pickBestSalesMapLayerSource(mapSources, 'network_fiber')?.source ?? null;
  const sourcesById = new Map(
    mapSources
      .filter((source): source is MapSource & { id: string } => Boolean(source.id))
      .map(source => [source.id, source]),
  );
  const seen = new Set<string>();
  return [...layers]
    .sort(compareInfrastructureLayers)
    .flatMap(accessPointLayer => {
      const sourceId = accessPointLayer.mapping_source_id?.trim();
      if (!sourceId) {
        return [];
      }
      const mapSource = sourcesById.get(sourceId);
      if (!mapSource) {
        return [];
      }
      const dedupeKey = `${accessPointLayer.layer_id}:${sourceId}`;
      if (seen.has(dedupeKey)) {
        return [];
      }
      seen.add(dedupeKey);
      return [
        {
          accessPointLayer,
          mapSource,
          fiberMapSource: findSalesMapVectorLayer(mapSource, 'network_fiber') ? mapSource : canonicalFiberSource,
        },
      ];
    });
}

// ─── UI primitives ────────────────────────────────────────────────────────────

function StatusBadge({ label, tone }: { label: string; tone: 'success' | 'info' | 'warning' | 'error' | 'muted' | 'default' }) {
  return <RegistryStatus label={label} variant={tone} />;
}

const defaultPagination: RoutePaginationState = { pageIndex: 0, pageSize: 10 };
const allowedPageSizes = new Set([10, 25, 50, 100]);
const allowedMarketStatuses = new Set(['active', 'inactive']);

type MarketsRegistry = 'zones' | 'markets' | 'groups';

interface MarketsRouteState {
  registry: MarketsRegistry;
  search: string;
  statuses: string[];
  sort: string[];
  pagination: RoutePaginationState;
}

const MARKET_SORT_FIELD_LISTS = {
  zones: ['created_at', 'markets', 'name', 'source', 'updated_at', 'zone_key', 'zone_kind'],
  markets: ['created_at', 'market_group', 'market_key', 'name', 'readiness', 'status', 'updated_at'],
  groups: ['created_at', 'markets', 'name', 'market_group_key', 'updated_at'],
} as const;
const marketSortFields: Record<MarketsRegistry, Set<string>> = {
  zones: new Set(MARKET_SORT_FIELD_LISTS.zones),
  markets: new Set(MARKET_SORT_FIELD_LISTS.markets),
  groups: new Set(MARKET_SORT_FIELD_LISTS.groups),
};

function parsePositiveInteger(value: string | null, fallback: number): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

function parseMarketsRouteState(path: string): MarketsRouteState {
  const queryIndex = path.indexOf('?');
  const params = new URLSearchParams(queryIndex >= 0 ? path.slice(queryIndex + 1) : '');
  const registryValue = params.get('registry');
  const registry: MarketsRegistry =
    registryValue === 'markets' || registryValue === 'groups' ? registryValue : 'zones';
  const requestedPageSize = parsePositiveInteger(params.get('page_size'), defaultPagination.pageSize);
  const pageSize = allowedPageSizes.has(requestedPageSize) ? requestedPageSize : defaultPagination.pageSize;
  const page = parsePositiveInteger(params.get('page'), 1);
  const statuses = registry === 'markets'
    ? Array.from(new Set(params.getAll('status').filter(value => allowedMarketStatuses.has(value)))).sort()
    : [];
  const sort = params.getAll('sort').map(value => value.trim()).filter(value => (
    marketSortFields[registry].has(value.startsWith('-') ? value.slice(1) : value)
  ));

  return {
    registry,
    search: params.get('q')?.trim() ?? '',
    statuses,
    sort,
    pagination: { pageIndex: page - 1, pageSize },
  };
}

function buildMarketsRoute(state: MarketsRouteState): string {
  const params = new URLSearchParams();
  const search = state.search.trim();
  if (search) params.set('q', search);
  if (state.registry !== 'zones') params.set('registry', state.registry);
  if (state.registry === 'markets') {
    state.statuses.slice().sort().forEach(status => params.append('status', status));
  }
  state.sort.forEach(value => params.append('sort', value));
  if (state.pagination.pageIndex > 0) params.set('page', String(state.pagination.pageIndex + 1));
  if (state.pagination.pageSize !== defaultPagination.pageSize) {
    params.set('page_size', String(state.pagination.pageSize));
  }
  const query = params.toString();
  return `/commerce/markets${query ? `?${query}` : ''}`;
}

function registryToListKind(registry: MarketsRegistry): WorkspaceEditorKind {
  if (registry === 'markets') return 'market';
  if (registry === 'groups') return 'group';
  return 'zone';
}

function listKindToRegistry(kind: WorkspaceEditorKind): MarketsRegistry {
  if (kind === 'market') return 'markets';
  if (kind === 'group') return 'groups';
  return 'zones';
}

function applyPaginationUpdate(
  current: RoutePaginationState,
  updater: RoutePaginationState | ((value: RoutePaginationState) => RoutePaginationState),
): RoutePaginationState {
  const next = typeof updater === 'function' ? updater(current) : updater;
  if (next.pageSize !== current.pageSize) {
    return {
      ...next,
      pageIndex: 0,
    };
  }
  return next;
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function MarketsPage() {
  const { activeTenant } = useScope();
  const navigation = useNavigation();
  const requestVersionRef = useRef(0);
  const selectorRequestVersionRef = useRef(0);
  const routeState = useMemo(() => parseMarketsRouteState(navigation.path), [navigation.path]);
  const activeListKind = registryToListKind(routeState.registry);
  const searchQuery = routeState.search;
  const marketStatusFilters = routeState.statuses;
  const pagination = routeState.pagination;
  const sorting = useMemo<SortingState>(() => routeState.sort.map(token => ({
    id: token.startsWith('-') ? token.slice(1) : token,
    desc: token.startsWith('-'),
  })), [routeState.sort]);

  const updateRouteState = useCallback((
    patch: Partial<MarketsRouteState>,
    options?: { replace?: boolean },
  ) => {
    const nextState: MarketsRouteState = {
      ...routeState,
      ...patch,
      pagination: patch.pagination ?? routeState.pagination,
      statuses: patch.statuses ?? routeState.statuses,
      sort: patch.sort ?? routeState.sort,
    };
    if (patch.registry && patch.registry !== routeState.registry) {
      nextState.pagination = { ...nextState.pagination, pageIndex: 0 };
      if (patch.registry !== 'markets') nextState.statuses = [];
      nextState.sort = [];
    }
    navigation.navigate(buildMarketsRoute(nextState), options);
  }, [navigation, routeState]);

  useEffect(() => {
    const canonicalRoute = buildMarketsRoute(routeState);
    if (navigation.path !== canonicalRoute) {
      navigation.navigate(canonicalRoute, { replace: true });
    }
  }, [navigation, routeState]);

  // Data
  const [zones, setZones] = useState<ServiceabilityZone[]>([]);
  const [markets, setMarkets] = useState<CommerceMarket[]>([]);
  const [groups, setGroups] = useState<MarketGroup[]>([]);
  const [selectorZones, setSelectorZones] = useState<ServiceabilityZone[]>([]);
  const [selectorMarkets, setSelectorMarkets] = useState<CommerceMarket[]>([]);
  const [selectorGroups, setSelectorGroups] = useState<MarketGroup[]>([]);
  const [zoneInfrastructureBindings, setZoneInfrastructureBindings] = useState<ZoneInfrastructureBinding[]>([]);
  const [totalZones, setTotalZones] = useState(0);
  const [totalMarkets, setTotalMarkets] = useState(0);
  const [totalGroups, setTotalGroups] = useState(0);
  const [signupEnabledCount, setSignupEnabledCount] = useState(0);
  const [marketStatusFacets, setMarketStatusFacets] = useState<Array<{ value: string; label?: string | null; count: number }>>([]);
  const marketStatusOptions = useMemo(() => {
    const buckets = new Map(marketStatusFacets.map(bucket => [bucket.value, bucket]));
    marketStatusFilters.forEach(value => {
      if (!buckets.has(value)) buckets.set(value, { value, label: null, count: 0 });
    });
    return Array.from(buckets.values()).map(bucket => ({
      label: bucket.label || bucket.value.charAt(0).toUpperCase() + bucket.value.slice(1),
      value: bucket.value,
    }));
  }, [marketStatusFacets, marketStatusFilters]);
  const marketStatusCounts = useMemo(
    () => Object.fromEntries(marketStatusOptions.map(option => [
      option.value,
      marketStatusFacets.find(bucket => bucket.value === option.value)?.count ?? 0,
    ])),
    [marketStatusFacets, marketStatusOptions],
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDeniedStatus, setAccessDeniedStatus] = useState<number | null>(null);

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorKind, setEditorKind] = useState<WorkspaceEditorKind>('zone');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [zoneForm, setZoneForm] = useState<ZoneFormState>(createEmptyZoneForm());
  const [marketForm, setMarketForm] = useState<MarketFormState>(createEmptyMarketForm());
  const [groupForm, setGroupForm] = useState<GroupFormState>(createEmptyGroupForm());

  const [zoneRowSelection, setZoneRowSelection] = useState<RowSelectionState>({});
  const [marketRowSelection, setMarketRowSelection] = useState<RowSelectionState>({});
  const [groupRowSelection, setGroupRowSelection] = useState<RowSelectionState>({});

  // ── Data loading ──────────────────────────────────────────────────────────

  const loadSelectorData = useCallback(async () => {
    const requestVersion = ++selectorRequestVersionRef.current;
    if (!activeTenant?.id) {
      setSelectorZones([]);
      setSelectorMarkets([]);
      setSelectorGroups([]);
      setZoneInfrastructureBindings([]);
      return;
    }
    const [selectorResult, infrastructureResult] = await Promise.allSettled([
      Promise.all([
        listServiceabilityZones({ catalogOwnerTenantId: activeTenant.id, limit: 1000, offset: 0 }),
        listMarkets({ catalogOwnerTenantId: activeTenant.id, limit: 1000, offset: 0 }),
        listMarketGroups({ catalogOwnerTenantId: activeTenant.id, limit: 1000, offset: 0 }),
      ]),
      Promise.all([
        listAccessPointLayers({
          catalogOwnerTenantId: activeTenant.id,
          status: 'active',
          purpose: 'quote_estimate',
          limit: 1000,
          offset: 0,
        }),
        listAccessPointLayers({
          catalogOwnerTenantId: activeTenant.id,
          status: 'active',
          purpose: 'business_quote_estimate',
          limit: 1000,
          offset: 0,
        }),
        listMapSources({ limit: 1000 }),
      ]),
    ]);
    if (requestVersion !== selectorRequestVersionRef.current) return;

    if (selectorResult.status === 'fulfilled') {
      const [
        zonePage,
        marketPage,
        groupPage,
      ] = selectorResult.value;
      setSelectorZones(zonePage.items);
      setSelectorMarkets(marketPage.items);
      setSelectorGroups(groupPage.items);
    } else {
      setSelectorZones([]);
      setSelectorMarkets([]);
      setSelectorGroups([]);
    }

    if (infrastructureResult.status === 'fulfilled') {
      const [quoteEstimateLayers, businessQuoteEstimateLayers, mapSources] = infrastructureResult.value;
      setZoneInfrastructureBindings(
        buildZoneInfrastructureBindings(
          [...quoteEstimateLayers.items, ...businessQuoteEstimateLayers.items],
          mapSources,
        ),
      );
    } else {
      setZoneInfrastructureBindings([]);
    }
  }, [activeTenant?.id]);

  const loadData = useCallback(async () => {
    const requestVersion = ++requestVersionRef.current;
    if (!activeTenant?.id) {
      setZones([]);
      setMarkets([]);
      setGroups([]);
      setTotalZones(0);
      setTotalMarkets(0);
      setTotalGroups(0);
      setSignupEnabledCount(0);
      setMarketStatusFacets([]);
      setError(null);
      setAccessDeniedStatus(null);
      return;
    }
    setLoading(true);
    setError(null);
    setAccessDeniedStatus(null);
    try {
      const [zonePage, marketPage, groupPage, signupPage] = await Promise.all([
        listServiceabilityZones({
          catalogOwnerTenantId: activeTenant.id,
          ...(activeListKind === 'zone'
            ? {
                search: searchQuery || undefined,
                sort: routeState.sort,
                ...toLimitOffset(pagination),
              }
            : { limit: 1, offset: 0 }),
        }),
        listMarkets({
          catalogOwnerTenantId: activeTenant.id,
          ...(activeListKind === 'market'
            ? {
                search: searchQuery || undefined,
                status: marketStatusFilters,
                sort: routeState.sort,
                include_facets: ['status'],
                ...toLimitOffset(pagination),
              }
            : { limit: 1, offset: 0 }),
        }),
        listMarketGroups({
          catalogOwnerTenantId: activeTenant.id,
          ...(activeListKind === 'group'
            ? {
                search: searchQuery || undefined,
                sort: routeState.sort,
                ...toLimitOffset(pagination),
              }
            : { limit: 1, offset: 0 }),
        }),
        listMarkets({ catalogOwnerTenantId: activeTenant.id, can_sign_up: true, limit: 1, offset: 0 }),
      ]);

      if (requestVersion !== requestVersionRef.current) return;

      const activePage = activeListKind === 'zone'
        ? zonePage
        : activeListKind === 'market'
          ? marketPage
          : groupPage;
      const nextPageIndex = lastPageIndexForTotal(activePage.total_count, pagination.pageSize);
      if (pagination.pageIndex > nextPageIndex) {
        updateRouteState({ pagination: { ...pagination, pageIndex: nextPageIndex } }, { replace: true });
        return;
      }

      setZones(activeListKind === 'zone' ? zonePage.items : []);
      setMarkets(activeListKind === 'market' ? marketPage.items : []);
      setGroups(activeListKind === 'group' ? groupPage.items : []);
      setTotalZones(zonePage.total_count);
      setTotalMarkets(marketPage.total_count);
      setTotalGroups(groupPage.total_count);
      setSignupEnabledCount(signupPage.total_count);
      setMarketStatusFacets(marketPage.facets.status ?? []);
    } catch (err) {
      if (requestVersion !== requestVersionRef.current) return;
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to load markets workspace.'));
      }
      setZones([]);
      setMarkets([]);
      setGroups([]);
      setTotalZones(0);
      setTotalMarkets(0);
      setTotalGroups(0);
      setSignupEnabledCount(0);
      setMarketStatusFacets([]);
    } finally {
      if (requestVersion === requestVersionRef.current) setLoading(false);
    }
  }, [
    activeTenant?.id,
    activeListKind,
    marketStatusFilters,
    pagination,
    routeState.sort,
    searchQuery,
    updateRouteState,
  ]);

  useEffect(() => { void loadData(); }, [loadData]);
  useEffect(() => { void loadSelectorData(); }, [loadSelectorData]);

  useEffect(() => {
    setZoneRowSelection({});
    setMarketRowSelection({});
    setGroupRowSelection({});
  }, [activeTenant?.id]);

  // ── Memos ─────────────────────────────────────────────────────────────────

  const zoneLabelsById = useMemo(() => {
    const map = new Map<string, string>();
    for (const z of selectorZones) map.set(z.zone_id, `${z.name} (${z.zone_key})`);
    return map;
  }, [selectorZones]);

  const zoneOptions = useMemo(() => buildServiceabilityZoneSelectorOptions(selectorZones), [selectorZones]);
  const marketGroupOptions = useMemo(() => buildMarketGroupSelectorOptions(selectorGroups), [selectorGroups]);
  const marketOptions = useMemo(() => buildMarketSelectorOptions(selectorMarkets, zoneLabelsById), [selectorMarkets, zoneLabelsById]);

  useEffect(() => {
    if (activeListKind === 'zone') {
      setZoneRowSelection({});
    } else if (activeListKind === 'market') {
      setMarketRowSelection({});
    } else {
      setGroupRowSelection({});
    }
  }, [activeListKind, marketStatusFilters, searchQuery]);

  // ── Editor openers ─────────────────────────────────────────────────────────

  const openCreateZone = () => {
    setEditorKind('zone');
    setEditingId(null);
    setZoneForm(createEmptyZoneForm());
    setEditorOpen(true);
  };

  const openCreateMarket = () => {
    if (selectorZones.length === 0) {
      toast.error('Create a serviceability zone before creating a market.');
      openCreateZone();
      return;
    }
    setEditorKind('market');
    setEditingId(null);
    const form = createEmptyMarketForm();
    form.zoneId = selectorZones[0]?.zone_id || '';
    setMarketForm(form);
    setEditorOpen(true);
  };

  const openCreateGroup = () => {
    setEditorKind('group');
    setEditingId(null);
    setGroupForm(createEmptyGroupForm());
    setEditorOpen(true);
  };

  const openEditZone = useCallback((item: ServiceabilityZone) => {
    setEditorKind('zone');
    setEditingId(item.zone_id);
    setZoneForm(buildZoneForm(item));
    setEditorOpen(true);
  }, []);

  const openEditMarket = useCallback((item: CommerceMarket) => {
    setEditorKind('market');
    setEditingId(item.market_id);
    setMarketForm(buildMarketForm(item));
    setEditorOpen(true);
  }, []);

  const openEditGroup = useCallback((item: MarketGroup) => {
    setEditorKind('group');
    setEditingId(item.market_group_id);
    setGroupForm(buildGroupForm(item));
    setEditorOpen(true);
  }, []);

  // ── Save ──────────────────────────────────────────────────────────────────

  const saveCurrentEditor = async () => {
    if (!activeTenant?.id) return;
    setSaving(true);
    setError(null);
    try {
      if (editorKind === 'zone') {
        const payload = { catalog_owner_tenant_id: activeTenant.id, ...buildZonePayload(zoneForm) };
        const saved = editingId
          ? await updateServiceabilityZone(editingId, payload)
          : await createServiceabilityZone(payload);
        setEditingId(saved.zone_id);
        setZoneForm(buildZoneForm(saved));
        toast.success(editingId ? 'Zone updated' : 'Zone created');
      } else if (editorKind === 'market') {
        const payload = { catalog_owner_tenant_id: activeTenant.id, ...buildMarketPayload(marketForm) };
        const saved = editingId
          ? await updateMarket(editingId, payload)
          : await createMarket(payload);
        setEditingId(saved.market_id);
        setMarketForm(buildMarketForm(saved));
        toast.success(editingId ? 'Market updated' : 'Market created');
      } else {
        const payload = { catalog_owner_tenant_id: activeTenant.id, ...buildGroupPayload(groupForm) };
        const saved = editingId
          ? await updateMarketGroup(editingId, payload)
          : await createMarketGroup(payload);
        setEditingId(saved.market_group_id);
        setGroupForm(buildGroupForm(saved));
        toast.success(editingId ? 'Market group updated' : 'Market group created');
      }
      await Promise.all([loadData(), loadSelectorData()]);
      setEditorOpen(false);
    } catch (err) {
      const msg = getCommerceErrorMessage(err, 'Failed to save market workspace changes.');
      setError(msg);
      toast.error('Failed to save', { description: msg });
    } finally {
      setSaving(false);
    }
  };

  // ── Derived values ─────────────────────────────────────────────────────────

  const metrics = useMemo(() => [
    { id: 'zones', label: 'Zones', value: totalZones },
    { id: 'markets', label: 'Markets', value: totalMarkets },
    { id: 'signup', label: 'Signup Enabled', value: signupEnabledCount },
    { id: 'groups', label: 'Market Groups', value: totalGroups },
  ], [signupEnabledCount, totalGroups, totalMarkets, totalZones]);

  const baseShellViewModel = buildCommerceShellViewModel({
    activeTenant, accessDeniedStatus, activeSectionId: 'markets',
    searchQuery: '', searchPlaceholder: 'Search markets...',
    onSearchChange: () => undefined, error,
    blockingReason: 'Your current role does not have permission to manage markets.',
    emptyStateLabel: 'Select a tenant to manage rollout markets.',
    onSelectSection: id => navigation.navigate(buildCommerceSectionRoute(id)),
    primaryAction: {
      id: totalZones === 0 ? 'create-zone' : 'create-market',
      label: totalZones === 0 ? 'New Zone' : 'New Market',
      onAction: totalZones === 0 ? openCreateZone : openCreateMarket,
      disabled: !activeTenant?.id || Boolean(accessDeniedStatus),
    },
    metrics: metrics.map(m => ({ id: m.id, label: m.label, value: String(m.value) })),
  });
  const shellViewModel = {
    ...baseShellViewModel,
    toolbar: { ...baseShellViewModel.toolbar, showSearch: false },
  };

  const handleNewForKind = (kind: WorkspaceEditorKind) => {
    if (kind === 'zone') openCreateZone();
    else if (kind === 'market') openCreateMarket();
    else openCreateGroup();
  };

  const zoneColumns = useMemo<ColumnDef<ServiceabilityZone>[]>(() => [
    {
      id: 'select',
      size: 40,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: 'name',
      accessorKey: 'name',
      size: 300,
      header: 'Zone',
      cell: ({ row }) => {
        const item = row.original;
        const configured = hasZoneBindings(item);
        return (
          <button
            type="button"
            onClick={() => openEditZone(item)}
            className="group min-w-0 text-left"
            aria-label={`Edit ${item.name}`}
          >
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-black tracking-tight text-foreground transition-colors group-hover:text-primary">
                {item.name}
              </p>
              <StatusBadge label={configured ? 'Configured' : 'Empty'} tone={configured ? 'success' : 'warning'} />
            </div>
            <DescriptionText className="text-[11px]">
              {item.zone_key} • {item.zone_kind}
            </DescriptionText>
          </button>
        );
      },
    },
    {
      id: 'source',
      accessorKey: 'source_system',
      header: 'Source',
      size: 150,
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">{row.original.source_system || 'Manual'}</span>
      ),
    },
    {
      id: 'markets',
      accessorKey: 'market_count',
      header: 'Markets',
      size: 100,
      cell: ({ row }) => (
        <span className="text-[11px] font-black text-primary">{row.original.market_count}</span>
      ),
    },
    {
      accessorKey: 'updated_at',
      header: 'Updated',
      size: 140,
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">
          {formatRelativeDate(row.original.updated_at)}
        </span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      size: 80,
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => (
        <div className="text-right">
          <button
            type="button"
            onClick={() => openEditZone(row.original)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
            aria-label={`Modify ${row.original.name}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </div>
      ),
    },
  ], [openEditZone]);

  const marketColumns = useMemo<ColumnDef<CommerceMarket>[]>(() => [
    {
      id: 'select',
      size: 40,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: 'name',
      accessorKey: 'name',
      size: 300,
      header: 'Market',
      cell: ({ row }) => {
        const item = row.original;
        return (
          <button
            type="button"
            onClick={() => openEditMarket(item)}
            className="group min-w-0 text-left"
            aria-label={`Edit ${item.name}`}
          >
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-black tracking-tight text-foreground transition-colors group-hover:text-primary">
                {item.name}
              </p>
              <StatusBadge label={item.status === 'active' ? 'Active' : 'Inactive'} tone={item.status === 'active' ? 'success' : 'muted'} />
            </div>
            <DescriptionText className="text-[11px]">
              {item.market_key} • {zoneLabelsById.get(item.zone_id) || item.zone_id}
            </DescriptionText>
          </button>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 100,
      cell: ({ row }) => <StatusBadge label={row.original.status} tone={row.original.status === 'active' ? 'success' : 'muted'} />,
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      id: 'readiness',
      accessorFn: market => Number(market.can_sign_up) * 2 + Number(market.can_schedule_install),
      header: 'Readiness',
      size: 150,
      cell: ({ row }) => (
        <HorizontalStack gap={1}>
          {row.original.can_sign_up && <StatusBadge label="Signup" tone="info" />}
          {row.original.can_schedule_install && <StatusBadge label="Install" tone="info" />}
          {!row.original.can_sign_up && !row.original.can_schedule_install && <span className="text-[10px] text-muted-foreground/60 italic">None</span>}
        </HorizontalStack>
      ),
    },
    {
      id: 'market_group',
      accessorFn: market => market.market_group_id ? 1 : 0,
      header: 'Market Group',
      size: 120,
      cell: ({ row }) => (
        <span className="text-[11px] font-black text-primary">{row.original.market_group_label || 'None'}</span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      size: 80,
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => (
        <div className="text-right">
          <button
            type="button"
            onClick={() => openEditMarket(row.original)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
            aria-label={`Modify ${row.original.name}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </div>
      ),
    },
  ], [openEditMarket, zoneLabelsById]);

  const groupColumns = useMemo<ColumnDef<MarketGroup>[]>(() => [
    {
      id: 'select',
      size: 40,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: 'name',
      accessorKey: 'name',
      size: 300,
      header: 'Market Group',
      cell: ({ row }) => {
        const item = row.original;
        return (
          <button
            type="button"
            onClick={() => openEditGroup(item)}
            className="group min-w-0 text-left"
            aria-label={`Edit ${item.name}`}
          >
            <p className="truncate text-sm font-black tracking-tight text-foreground transition-colors group-hover:text-primary">
              {item.name}
            </p>
            <DescriptionText className="text-[11px]">
              {item.market_group_key}
            </DescriptionText>
          </button>
        );
      },
    },
    {
      id: 'markets',
      accessorKey: 'market_count',
      header: 'Markets',
      size: 100,
      cell: ({ row }) => (
        <span className="text-[11px] font-black text-primary">{row.original.market_count}</span>
      ),
    },
    {
      accessorKey: 'updated_at',
      header: 'Updated',
      size: 140,
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">
          {formatRelativeDate(row.original.updated_at)}
        </span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      size: 80,
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => (
        <div className="text-right">
          <button
            type="button"
            onClick={() => openEditGroup(row.original)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
            aria-label={`Modify ${row.original.name}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </div>
      ),
    },
  ], [openEditGroup]);
  defineCommerceRegistryColumns(zoneColumns, { mode: 'manual', sortFields: MARKET_SORT_FIELD_LISTS.zones, registry: 'serviceability-zones' });
  defineCommerceRegistryColumns(marketColumns, { mode: 'manual', sortFields: MARKET_SORT_FIELD_LISTS.markets, registry: 'markets' });
  defineCommerceRegistryColumns(groupColumns, { mode: 'manual', sortFields: MARKET_SORT_FIELD_LISTS.groups, registry: 'market-groups' });

  // ── Render ────────────────────────────────────────────────────────────────

  const listCounts: Record<WorkspaceEditorKind, number> = {
    zone: totalZones, market: totalMarkets, group: totalGroups,
  };

  const handlePaginationChange = (updater: RoutePaginationState | ((value: RoutePaginationState) => RoutePaginationState)) => {
    const next = applyPaginationUpdate(pagination, updater);
    updateRouteState({ pagination: next });
  };

  const handleSortingChange = (updater: SortingState | ((value: SortingState) => SortingState)) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    updateRouteState({
      sort: next.map(item => `${item.desc ? '-' : ''}${item.id}`),
      pagination: { ...pagination, pageIndex: 0 },
    });
  };

  const handleSearchChange = (value: string) => {
    updateRouteState({
      search: value.trim(),
      pagination: { ...pagination, pageIndex: 0 },
    }, { replace: true });
  };

  const handleRegistryChange = (kind: WorkspaceEditorKind) => {
    updateRouteState({ registry: listKindToRegistry(kind) });
  };

  const hasActiveQuery = Boolean(searchQuery || marketStatusFilters.length > 0);

  return (
    <CommerceCatalogWorkspace
      viewModel={shellViewModel}
      padded={false}
      takeoverActive={editorOpen}
      replaceShellHeaderWhenTakeoverActive
      takeoverContent={
        <MarketsWizardEditor
          isOpen={editorOpen}
          kind={editorKind}
          editingId={editingId}
          zoneForm={zoneForm}
          marketForm={marketForm}
          groupForm={groupForm}
          setZoneForm={setZoneForm}
          setMarketForm={setMarketForm}
          setGroupForm={setGroupForm}
          onSave={saveCurrentEditor}
          onClose={() => setEditorOpen(false)}
          saving={saving}
          zoneOptions={zoneOptions}
          marketOptions={marketOptions}
          marketGroupOptions={marketGroupOptions}
          zoneInfrastructureBindings={zoneInfrastructureBindings}
        />
      }
      titleContent={
        <CommerceBreadcrumbHeader
          breadcrumbs={[{ label: 'Markets' }]}
          title="Markets"
          description="Manage serviceability zones, rollout readiness, and market groups."
        />
      }
    >
      <RegistryWorkspace>
        <CommerceRegistryUtilityBand className="shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="flex rounded-lg border border-border/50 bg-muted/20 p-1 gap-0.5">
              {([
                { key: 'zone' as const, label: 'Zones', icon: <Layers className="h-3 w-3" /> },
                { key: 'market' as const, label: 'Markets', icon: <Store className="h-3 w-3" /> },
                { key: 'group' as const, label: 'Market Groups', icon: <Tag className="h-3 w-3" /> },
              ] as const).map(({ key, label, icon }) => {
                const isActive = activeListKind === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleRegistryChange(key)}
                    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[10px] font-black uppercase tracking-tight transition-all ${
                      isActive
                        ? 'bg-background text-foreground shadow-sm border border-border/50'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {icon}
                    <span className="hidden sm:inline">{label}</span>
                    <span className={`tabular-nums text-[9px] font-black ${isActive ? 'text-primary/70' : 'text-muted-foreground/40'}`}>
                      {listCounts[key]}
                    </span>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => handleNewForKind(activeListKind)}
              disabled={activeListKind === 'market' && selectorZones.length === 0}
              className="inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/8 px-3 py-1.5 text-[10px] font-black uppercase tracking-tight text-foreground transition-colors hover:bg-primary/15 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Plus className="h-3.5 w-3.5 text-primary" />
              <span className="hidden sm:inline">
                New {activeListKind === 'zone' ? 'Zone' : activeListKind === 'market' ? 'Market' : 'Market Group'}
              </span>
            </button>
          </div>
        </CommerceRegistryUtilityBand>

        {activeListKind === 'zone' && (
          <RegistryTableSurface>
            <DataTable
              mode="manual"
              columns={zoneColumns}
              data={zones}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              rowSelection={zoneRowSelection}
              onRowSelectionChange={setZoneRowSelection}
              pagination={pagination}
              onPaginationChange={handlePaginationChange}
              manualPagination={true}
              totalRowCount={totalZones}
              loading={loading}
              className="border-none rounded-none"
              paginationClassName="px-0 py-0"
              showPagination={true}
              emptyState={
                <div className="px-0 py-12 text-center">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted/30">
                    <Layers className="h-6 w-6 text-muted-foreground/60" />
                  </div>
                  <p className="text-sm font-black text-foreground">No Serviceability Zones</p>
                  <DescriptionText className="mt-1 text-xs">
                    {hasActiveQuery
                      ? 'No serviceability zones matched the current search.'
                      : 'Create the first zone to define geography for rollout markets.'}
                  </DescriptionText>
                </div>
              }
              renderToolbar={(table) => (
                <DataTableToolbar
                  table={table}
                  searchValue={searchQuery}
                  onSearchChange={handleSearchChange}
                  searchDebounceMs={250}
                  searchPlaceholder="Search serviceability zones..."
                  resultCount={totalZones}
                  className="border-b border-border/40 bg-transparent px-0 py-0"
                />
              )}
            />
          </RegistryTableSurface>
        )}

        {activeListKind === 'market' && (
          <RegistryTableSurface>
            <DataTable
              mode="manual"
              columns={marketColumns}
              data={markets}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              rowSelection={marketRowSelection}
              onRowSelectionChange={setMarketRowSelection}
              pagination={pagination}
              onPaginationChange={handlePaginationChange}
              manualPagination={true}
              totalRowCount={totalMarkets}
              loading={loading}
              className="border-none rounded-none"
              paginationClassName="px-0 py-0"
              showPagination={true}
              emptyState={
                <div className="px-0 py-12 text-center">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted/30">
                    <Store className="h-6 w-6 text-muted-foreground/60" />
                  </div>
                  <p className="text-sm font-black text-foreground">No Markets</p>
                  <DescriptionText className="mt-1 text-xs">
                    {hasActiveQuery
                      ? 'No markets matched the current search or status filters.'
                      : totalZones === 0
                      ? 'Create a zone first, then attach a market.'
                      : 'Create the first market to define rollout readiness.'}
                  </DescriptionText>
                </div>
              }
              renderToolbar={(table) => (
                <DataTableToolbar
                  table={table}
                  searchValue={searchQuery}
                  onSearchChange={handleSearchChange}
                  searchDebounceMs={250}
                  searchPlaceholder="Search markets..."
                  resultCount={totalMarkets}
                  className="border-b border-border/40 bg-transparent px-0 py-0"
                  filters={
                    table.getColumn('status') && (
                      <DataTableFacetedFilter
                        title="Status"
                        selectedValues={new Set(marketStatusFilters)}
                        onFilterChange={(values) => updateRouteState({
                          statuses: Array.from(values),
                          pagination: { ...pagination, pageIndex: 0 },
                        })}
                        options={marketStatusOptions}
                        counts={marketStatusCounts}
                      />
                    )
                  }
                />
              )}
            />
          </RegistryTableSurface>
        )}

        {activeListKind === 'group' && (
          <RegistryTableSurface>
            <DataTable
              mode="manual"
              columns={groupColumns}
              data={groups}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              rowSelection={groupRowSelection}
              onRowSelectionChange={setGroupRowSelection}
              pagination={pagination}
              onPaginationChange={handlePaginationChange}
              manualPagination={true}
              totalRowCount={totalGroups}
              loading={loading}
              className="border-none rounded-none"
              paginationClassName="px-0 py-0"
              showPagination={true}
              emptyState={
                <div className="px-0 py-12 text-center">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted/30">
                    <Tag className="h-6 w-6 text-muted-foreground/60" />
                  </div>
                  <p className="text-sm font-black text-foreground">No Market Groups</p>
                  <DescriptionText className="mt-1 text-xs">
                    {hasActiveQuery
                      ? 'No market groups matched the current search.'
                      : 'Create a market group for eligibility and policy targeting.'}
                  </DescriptionText>
                </div>
              }
              renderToolbar={(table) => (
                <DataTableToolbar
                  table={table}
                  searchValue={searchQuery}
                  onSearchChange={handleSearchChange}
                  searchDebounceMs={250}
                  searchPlaceholder="Search market groups..."
                  resultCount={totalGroups}
                  className="border-b border-border/40 bg-transparent px-0 py-0"
                />
              )}
            />
          </RegistryTableSurface>
        )}
      </RegistryWorkspace>
    </CommerceCatalogWorkspace>
  );
}
